import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { createSearchParams, useSearchParams, useNavigate } from 'react-router-dom';
import { objectToQueryString, apiHelper, baseUrl } from 'helpers/util';
import { notify } from 'helpers/globalFunctions';
import { DateTime } from 'luxon';

import ColumnBox from 'components/ColumnBox';
import RowBox from 'components/RowBox';
import CardList from 'components/CardList';
import CardItem from 'components/CardItem';
import SmallestHeading from 'components/SmallestHeading';
import SmallHeading from 'components/SmallHeading';
import StoreMenu from 'modules/StoreManager/StoreMenu';


export default function Form(props) {
    const warehouse = props.warehouse;

    const navigate = useNavigate();
    const [ mealCountsId, setMealCountsId ] = useState(null);

    const [ entryDate, setEntryDate ] = useState(DateTime.now());
    const [ count, setCount ] = useState('');

    const [ menuOptions, setMenuOptions ] = useState([]);
    const [ menuHash, setMenuHash ] = useState({});
    const [ menu, setMenu ] = useState(null);

    const [ purposeOptions, setPurposeOptions ] = useState([]);
    const [ purpose, setPurpose ] = useState(null);

    const [ remarks, setRemarks ] = useState('');

    const [ mealCounts, setMealCounts ] = useState([]);

    const [ isFormSubmitting, setIsFormSubmitting ] = useState(false);
    const [ isMenuDialogOpen, setIsMenuDialogOpen ] = useState(false);
    const formErrorState = useRef({})

    const fillMealCountsForm = function(mealCountDetails) {
        setMealCountsId(mealCountDetails ? mealCountDetails.name : null);
        setEntryDate(mealCountDetails ? DateTime.fromSQL(mealCountDetails.entry_datetime)
            : DateTime.now());
        setPurpose(mealCountDetails ? mealCountDetails.purpose : null);
        setMenu(mealCountDetails ? mealCountDetails.menu : null);
        setCount(mealCountDetails ? mealCountDetails.count : '');
        setRemarks(mealCountDetails ? mealCountDetails.remarks : '');
    }

    const loadMealCountsData = function() {
        const apiParams = {
            "doctype": "Store Meal Counts",
            "fields": JSON.stringify(["name", "menu", "count", "entry_datetime", "purpose"]),
            "order_by": "entry_datetime desc, purpose asc",
            "limit": 1000
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) { setMealCounts(response.json.data); }
            else { console.log(response.json); }
        })
    }

    const loadPurposeOptions = function() {
        const apiParams = {
            "doctype": "Store Purpose",
            "fields": JSON.stringify(["name"]),
            "order_by": "name asc",
            "limit": 1000,
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) { setPurposeOptions(response.json.data.map(el => el.name)); }
            else { console.log(response.json); }
        })
    }

    const loadMenuOptions = function() {
        if (!entryDate) { setMenuOptions([]) }
        const apiParams = {
            "doctype": "Store Menu",
            "fields": JSON.stringify(["name", "menu_type", "menu_date"]),
            "order_by": "menu_type asc",
            "limit": 1000,
            "filters": JSON.stringify([["menu_date", "=", entryDate.toFormat('yyyy-MM-dd')]])
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                setMenuOptions(response.json.data.map(el => el.name));
                const menuHash = {};
                for (const r of response.json.data) { menuHash[r.name] = r; }
                setMenuHash(menuHash);
            } else { console.log(response.json); }
        })
    }

    const checkForFormErrors = function(field, isError) {
        formErrorState.current[field] = isError
        return isError;
    }

    const onClickSave = function() {
        for (const f in formErrorState.current) {
            if (formErrorState.current[f]) {
                notify.error({ description: 'Please fill all details' });
                return;
            }
        }

        const payload = {
            'entry_datetime': entryDate.toFormat('yyyy-MM-dd HH:mm:ss'),
            menu, purpose, remarks, count
        }

        const apiParams = { "doctype": "Store Meal Counts" };
        mealCountsId && (apiParams["name"] = mealCountsId)

        setIsFormSubmitting(true);
        let apiUrl = `${baseUrl}/.netlify/functions/erp_api`;
        let apiPromise = null;
        if (mealCountsId) {
            apiPromise = apiHelper.put({ "url": apiUrl, "params": apiParams, "data": payload })
        } else {
            apiPromise = apiHelper.post({ "url": apiUrl, "params": apiParams, "data": payload })
        }
        apiPromise
        .then(response => {
            if (response.status) {
                setIsFormSubmitting(false);
                fillMealCountsForm();
                loadMealCountsData();
                notify.success({ "description": "Meal Count successfully added !" })
            } else { console.log(response.json); setIsFormSubmitting(false); }
        })
    }

    const handleMenuDialogClose = function() {
        setIsMenuDialogOpen(false);
    }

    useEffect(function() {
        if (!isMenuDialogOpen) { loadMenuOptions(); }
    }, [entryDate, isMenuDialogOpen]);

    useEffect(function() {
        loadMealCountsData();
        loadPurposeOptions();
    }, [])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center',
            padding: '15px' }}>
            <ColumnBox>
                <SmallHeading>Add / Edit Meal Counts</SmallHeading>
                <ColumnBox sx={{ alignItems: 'center', maxWidth: '500px',
                    width: '100%' }}>
                    <Box sx={{ marginTop: '15px', width: '100%' }}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <MobileDateTimePicker
                                onChange={(newValue) => { setEntryDate(newValue); }}
                                value={entryDate}
                                inputFormat="dd-MMM-yyyy hh:mm a"
                                closeOnSelect={true} disableMaskedInput={true}
                                renderInput={(params) => <TextField {...params} required
                                    error={checkForFormErrors('entry_datetime', !entryDate)}
                                    label="Entry Date" size="small" sx={{ width: '100%' }} />}
                            />
                        </LocalizationProvider>
                    </Box>
                    <ColumnBox sx={{ width: '100%', marginTop: '15px' }}>
                        <Autocomplete
                            disablePortal
                            size="small"
                            onChange={(e, newValue) => { setMenu(newValue); }}
                            options={menuOptions}
                            value={menu}
                            getOptionLabel={el => (menuHash[el] && menuHash[el]['menu_type'])
                                ? menuHash[el]['menu_type'] : el}
                            renderInput={(params) => <TextField required
                                error={checkForFormErrors('menu', !menu)}
                                {...params} label="Menu" />}
                        />
                        <Box sx={{ alignSelf: 'flex-end', color: '#1976d2',
                            cursor: 'pointer' }} onClick={ () => setIsMenuDialogOpen(true) }>
                                Add new ?</Box>
                    </ColumnBox>
                    <Autocomplete
                        disablePortal
                        size="small"
                        onChange={(e, newValue) => { setPurpose(newValue); }}
                        options={purposeOptions}
                        value={purpose}
                        sx={{ width: '100%', marginTop: '15px' }}
                        renderInput={(params) => <TextField required
                            error={checkForFormErrors('purpose', !purpose)}
                            {...params} label="Purpose" />}
                    />
                    <TextField label={"Count"}
                        onChange={(e) => setCount(e.target.value)}
                        value={count} required
                        error={checkForFormErrors('count', count === '' || count === null)}
                        sx={{ width: '100%', marginTop: '15px' }}
                        type="number"
                        size="small"
                    />
                    <TextField label="Remarks"
                        sx={{ width: '100%', marginTop: '15px' }}
                        minRows={2}
                        onChange={(e) => { setRemarks(e.target.value); }}
                        value={remarks}
                        multiline={true} />

                    { isFormSubmitting ? <CircularProgress sx={{ marginTop: '15px' }} /> :
                        <Button sx={{ marginTop: '15px' }} onClick={() => onClickSave()}
                            variant="outlined">Save</Button>
                    }
                </ColumnBox>
                <SmallHeading>Meal Count Entries</SmallHeading>
                <CardList sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    { mealCounts.map(m => (
                        <CardItem sx={{ 'cursor': 'pointer', 'minWidth': '70px',
                            'width': 'unset', 'maxWidth': '200px', 'margin': '10px 5px' }}
                            onClick={() => fillMealCountsForm(m)}>
                            <Box sx={{ fontWeight: 'bold' }}>
                                {DateTime.fromSQL(m.entry_datetime).toFormat('dd MMM hh:mm a')}</Box>
                            <RowBox>
                                <Box>{`${m.purpose}: ${m.count}`}</Box>
                            </RowBox>
                        </CardItem>
                    )) }
                </CardList>
            </ColumnBox>
            <Dialog open={isMenuDialogOpen} onClose={handleMenuDialogClose}>
                <DialogTitle>Add new Menu</DialogTitle>
                <DialogContent>
                    <StoreMenu />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMenuDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
